import React, { useEffect, useState } from 'react';
import { partyPatronService } from '../../services';
import { AdminPageHeader, Avatar, Card, DataTable, DropChip, EmptyLine, Loading, SegTrack, SubTabs, money, shortDate } from '../../components/admin/shell';
import { ColumnChart, fmt } from '../../components/admin/analytics/charts';

/**
 * MARKETING › PATRON REPORTS — spec/sections.md §17. Two readings of the same four
 * conversion types. BY BRAND is every brand across every patron day it has run
 * (`GET api/partypatrons/reports/brands`); BY DAY is one slot — its totals, its conversion
 * counts and the fortnight after it. Read-only.
 */
const RANGES = [{ value: 30, label: '30D' }, { value: 90, label: '90D' }, { value: 0, label: 'ALL TIME' }];
/** A rate is blank, never 0%, when nothing was opened — the two are different answers. */
const pct = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);

export default function ReportsPage() {
  const [tab, setTab] = useState('brands');
  const [days, setDays] = useState(0);
  const [brands, setBrands] = useState(null);
  const [slots, setSlots] = useState(null);
  const [selected, setSelected] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await partyPatronService.list();
      setSlots(r.success ? (r.data || []) : []);
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setBrands(null);
    (async () => {
      const r = await partyPatronService.brandReport(days);
      if (!cancelled) setBrands(r.success ? (r.data || []) : []);
    })();
    return () => { cancelled = true; };
  }, [days]);

  useEffect(() => {
    if (!selected) { setReport(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const r = await partyPatronService.getReport(selected);
      if (cancelled) return;
      setLoading(false);
      setReport(r.success ? r.data : null);
    })();
    return () => { cancelled = true; };
  }, [selected]);

  const options = [
    { value: '', label: 'PICK ONE' },
    ...(slots || []).map((p) => ({ value: p.id, label: `${shortDate(p.dayActive)} · ${p.brandName} · ${p.regionName || p.regionSlug || ''}`.toUpperCase() })),
  ];
  const counts = Object.entries(report?.counts || {});
  const totals = (brands || []).reduce((a, b) => ({ days: a.days + b.patronDays, gifted: a.gifted + b.giftedCents }), { days: 0, gifted: 0 });

  const brandTable = (
    <DataTable
      title="BY BRAND"
      right={brands ? `${totals.days} PATRON ${totals.days === 1 ? 'DAY' : 'DAYS'} · ${money(totals.gifted)} GIFTED` : null}
      columns={[
        {
          key: 'brandName',
          label: 'BRAND',
          fr: 1.8,
          render: (b) => (
            <span className="flex min-w-0 items-center gap-2">
              <Avatar url={b.logoUrl} name={b.brandName} size={28} />
              <span className="truncate">{b.brandName}</span>
            </span>
          ),
        },
        { key: 'patronDays', label: 'DAYS', fr: 0.6, right: true, render: (b) => `${b.dispatchedDays}/${b.patronDays}` },
        { key: 'regions', label: 'AREAS', fr: 0.6, right: true, render: (b) => fmt(b.regions) },
        { key: 'peopleReached', label: 'PEOPLE', fr: 0.8, right: true, render: (b) => fmt(b.peopleReached) },
        { key: 'opens', label: 'OPENS', fr: 0.7, right: true, render: (b) => fmt(b.opens) },
        { key: 'clicks', label: 'CLICKS', fr: 0.9, right: true, render: (b) => `${fmt(b.clicks)} · ${pct(b.clickRate)}` },
        { key: 'giftedCents', label: 'GIFTED', fr: 0.9, right: true, render: (b) => money(b.giftedCents) },
        { key: 'spentCents', label: 'SPENT', fr: 1, right: true, render: (b) => `${money(b.spentCents)} · ${pct(b.spendRate)}` },
        { key: 'lastDayActive', label: 'LAST RUN', fr: 0.9, render: (b) => (b.lastDayActive ? shortDate(b.lastDayActive) : 'NEVER') },
      ]}
      rows={brands || []}
      loading={brands == null}
      rowKey={(b) => b.brandId}
      empty="NO BRAND HAS RUN A PATRON DAY YET."
      footerLeft={brands ? `${brands.length} ${brands.length === 1 ? 'brand' : 'brands'} · a rate is blank when nothing was opened, which is not the same as nobody clicking` : ''}
    />
  );

  return (
    <>
      <AdminPageHeader section="reports" meta="What a brand's patron days bought it. The same four conversions, read by brand or by day." viewOnly={false} />
      <div className="flex flex-col gap-4">
        <SubTabs
          tabs={[{ key: 'brands', label: 'BY BRAND' }, { key: 'days', label: 'BY DAY' }]}
          active={tab}
          onChange={setTab}
          actions={tab === 'brands' ? <SegTrack ariaLabel="Range" value={days} onChange={setDays} options={RANGES} /> : null}
        />
        {tab === 'brands' && brandTable}
        <div className={`flex flex-wrap items-center gap-2 ${tab === 'brands' ? 'hidden' : ''}`}>
          {slots == null ? <Loading /> : <DropChip label="PATRON" value={selected} onChange={setSelected} options={options} />}
          {slots && slots.length === 0 && <span className="font-mono text-[11px] text-[#6B7280]">No patron days scheduled yet.</span>}
        </div>
        {tab === 'days' && loading && <div className="rounded-[24px] bg-white p-5"><Loading /></div>}
        {tab === 'days' && !loading && !report && slots && slots.length > 0 && (
          <div className="rounded-[24px] bg-white p-3"><EmptyLine>PICK A PATRON DAY TO SEE ITS REPORT.</EmptyLine></div>
        )}
        {tab === 'days' && !loading && report && (
          <Card title={`REPORT · ${report.brandName || '—'} · ${shortDate(report.dayActive)}`} bodyClass="px-5 pb-5">
            <dl className="grid grid-cols-1 gap-2 md:grid-cols-3">
              {[
                ['BRAND', report.brandName || '—'],
                ['REGION', report.regionName || '—'],
                ['DAY', shortDate(report.dayActive)],
                ['DISPATCHED', report.dispatchedAt ? new Date(report.dispatchedAt).toLocaleString() : 'no'],
                ['GUEST GIFT', money(report.guestGiftCents)],
                ['HOST GIFT', money(report.hostGiftCents)],
              ].map(([k, v]) => (
                <div key={k} className="flex flex-col gap-1 rounded-[14px] bg-[#F7F7F7] px-4 py-3">
                  <dt className="font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-[#6B7280]">{k}</dt>
                  <dd className="font-mono text-[12px] font-bold">{v}</dd>
                </div>
              ))}
            </dl>
            {(report.byDay || []).some((d) => d.total > 0) && (
              <div className="pt-5">
                <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-[#6B7280]">CONVERSIONS PER DAY</div>
                <ColumnChart
                  height={160}
                  items={report.byDay.map((d) => ({
                    key: d.date,
                    label: dayLabel(d.date),
                    value: d.total,
                    tip: [['CONVERSIONS', fmt(d.total)]],
                    breakdownLabel: 'WHAT HAPPENED',
                    breakdown: Object.entries(d.types || {}).map(([k, v]) => ({ label: k.replace(/_/g, ' ').toUpperCase(), value: v, display: fmt(v) })),
                    source: 'brand_conversion_events WHERE party_patron_id = this slot GROUP BY day',
                  }))}
                  axisInfo={{
                    title: 'CONVERSIONS',
                    kicker: 'PER DAY',
                    desc: 'A notification opened, a promo link clicked, a gift credited, a gift spent — the four things a patron day can cause.',
                    source: 'brand_conversion_events WHERE party_patron_id = this slot',
                  }}
                />
              </div>
            )}
            <div className="pt-5">
              <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-[#6B7280]">CONVERSION EVENTS</div>
              {counts.length === 0 ? (
                <div className="font-mono text-[12px] text-[#6B7280]">No events tracked yet.</div>
              ) : (
                <ul className="list-none font-mono text-[12px] leading-[1.8]">
                  {counts.map(([k, v]) => (
                    <li key={k} className="flex justify-between gap-4 border-b border-[#F2F2F2] py-1 last:border-b-0">
                      <span className="uppercase">{k.replace(/_/g, ' ')}</span>
                      <span className="text-right">{v.count} ({money(v.total)})</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
        )}
      </div>
    </>
  );
}

/** "OCT 14" for a day on the conversions chart. */
function dayLabel(iso) {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.toLocaleString('en', { month: 'short' }).toUpperCase()} ${d.getDate()}`;
}
